import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AdminModal } from '../../../components/admin/admin-modal';
import AdminTextInput from '../../../components/admin/admin-text-input';

export function AddManufacturerUserModal({ show, onClose, handleAddManufacturer, initialData }) {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [organization, setOrganization] = useState('');
    const [password, setPassword] = useState('');
    const [validation, setValidation] = useState({});
    const [displayError, setDisplayError] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (show && initialData) {
            setFullName(initialData.fullName || '');
            setEmail(initialData.email || '');
            setOrganization(initialData.organization || '');
            setPassword('');
        }
    }, [show, initialData]);

    const handleSave = () => {
        const isEmpty = (value) => !value || value.trim() === '';

        const validate = {
            fullName: !isEmpty(fullName),
            email: !isEmpty(email),
            organization: !isEmpty(organization),
        };

        const isValid = Object.values(validate).every(Boolean);
        setValidation((prev) => ({ ...prev, ...validate }));

        if (!isValid) return;

        const data = {
            id: initialData?.id || null,
            fullName,
            email,
            organization,
            password,
        };
        handleAddManufacturer(
            data,
            () => {
                setFullName('');
                setEmail('');
                setOrganization('');
                setPassword('');
                onClose();
            },
            (err) => {
                setDisplayError(err);
            }
        );
    };

    const onCancel = () => {
        setFullName('');
        setEmail('');
        setOrganization('');
        setPassword('');
        setDisplayError('');
        setValidation({});
        onClose();
    };

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [show]);

    if (!show) return null;

    return (
        <AdminModal
            show={true}
            onClose={onCancel}
            onSave={handleSave}
            modalTitle={initialData?.id ? 'Edit Manufacturer' : 'Add Manufacturer'}
            displayError={displayError}
            onHideError={() => {
                setDisplayError('');
            }}
            label="Manufacturer Name"
        >
            <div className="admin-modal-row">
                <div className="admin-modal-cell">
                    <AdminTextInput
                        id="full-name"
                        label="First & Last Name"
                        isRequired={true}
                        value={fullName}
                        validated={validation.fullName}
                        onChange={(value) => {
                            setValidation({ ...validation, fullName: value !== '' });
                            setFullName(value);
                        }}
                        requiredText="First & Last Name is required"
                    />
                </div>
                <div className="admin-modal-cell">
                    <AdminTextInput
                        id="email-address"
                        label="Email Address"
                        isRequired={true}
                        value={email}
                        validated={validation.email}
                        onChange={(value) => {
                            setValidation({ ...validation, email: value !== '' });
                            setEmail(value);
                        }}
                        requiredText="Email Address is required"
                    />
                </div>
            </div>

            <div className="admin-modal-row">
                <div className="admin-modal-cell">
                    <AdminTextInput
                        id="organization-name"
                        label="Organization"
                        isRequired={true}
                        value={organization}
                        validated={validation.organization}
                        onChange={(value) => {
                            setValidation({ ...validation, organization: value !== '' });
                            setOrganization(value);
                        }}
                        requiredText="Organization is required"
                    />
                </div>
            </div>

            <div className="admin-modal-row">
                <div className="admin-modal-cell">
                    <AdminTextInput
                        id="password"
                        label="Password"
                        inputType="password"
                        placeholder="Leave blank to let user create their own password"
                        isRequired={false}
                        value={password}
                        onChange={(value) => setPassword(value)}
                    />
                </div>
            </div>
        </AdminModal>
    );
}

AddManufacturerUserModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    handleAddManufacturer: PropTypes.func.isRequired,
    initialData: PropTypes.object,
};
